import React, { forwardRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircleFlag } from 'react-circle-flags';
import { Grid, Stack, Tooltip, Typography } from '@mui/material';

import { BaseSection } from 'shared/sections/BaseSection/BaseSection';
import { SectionTitle } from 'shared/sections/SectionTitle/SectionTitle';
import { SectionCTA } from 'shared/components/SectionCTA/SectionCTA';
import { FadeInOnScroll } from 'shared/components/FadeInOnScroll/FadeInOnScroll';
import { LocalizationContext } from 'contexts/LocalizationContext/LocalizationContext';
import { LayoutContext } from 'contexts/LayoutContext/LayoutContext';
import { members } from 'data/team.data';
import { MenuItemElement } from 'contexts/LayoutContext/LayoutContext.types';

const FLAG_NAMES: Record<string, string> = {
  it: 'Italy', fr: 'France', lb: 'Lebanon', ve: 'Venezuela',
  ch: 'Switzerland', pl: 'Poland', be: 'Belgium', us: 'United States',
  pt: 'Portugal', zw: 'Zimbabwe', ua: 'Ukraine', eu: 'Europe',
};

export const TeamSection = forwardRef<MenuItemElement>((props, ref) => {
  const navigate = useNavigate();
  const {dictionary} = useContext(LocalizationContext);
  const { contactUsSectionRef } = useContext(LayoutContext);
  const { isMobile } = useContext(LayoutContext);

  return (
    <BaseSection ref={ref} style={{ backgroundImage: 'linear-gradient(to bottom, #000 0%, transparent 120px)' }}>
      <Grid item style={{width: '100%', textAlign: 'center'}}>
        <FadeInOnScroll>
          <SectionTitle onEnterViewport={() => navigate('/team')}>
            {dictionary.teamSectionTitle}
          </SectionTitle>
          <Typography variant="h6" color="textSecondary" sx={{ mt: 1, mb: 2, px: 2 }}>
            Real people who care. We bring ownership, passion and experience to every product we touch.
          </Typography>
        </FadeInOnScroll>
      </Grid>
      <Grid item>
        <Stack direction="row" flexWrap="wrap" justifyContent="center">
          {members.map(({title, subtitle, image, flags }, i) => (
            <Stack
              key={i}
              alignItems="center"
              direction="column"
              sx={{ width: isMobile ? 150 : 220, m: isMobile ? '15px' : '30px' }}
            >
              <img
                src={image}
                alt={title}
                width={isMobile ? 100 : 200}
                height={isMobile ? 100 : 200}
                style={{
                  objectFit: 'cover',
                  borderRadius: '50%',
                }}
              />
              <Typography variant="h5" sx={{ mt: 1 }}>{title}</Typography>
              <Typography
                variant="body1"
                sx={{ textAlign: 'center', ...(isMobile && { fontSize: '0.8rem' }) }}
              >
                {subtitle}
              </Typography>
              {flags && flags.length > 0 && (
                <Stack direction="row" gap={1} sx={{ p: 1 }}>
                  {flags.map((code, j) => (
                    <Tooltip key={j} title={FLAG_NAMES[code] || code.toUpperCase()} arrow>
                      <span style={{ display: 'inline-flex' }}>
                        <CircleFlag
                          countryCode={code}
                          cdnUrl="/flags/circle/"
                          height={isMobile ? 18 : 22}
                          width={isMobile ? 18 : 22}
                        />
                      </span>
                    </Tooltip>
                  ))}
                </Stack>
              )}
            </Stack>
          ))}
        </Stack>
      </Grid>
      <SectionCTA
        nextSectionLabel="Contact Us"
        onNextSection={() => contactUsSectionRef?.current?.scrollIntoView({ behavior: 'smooth' })}
      />
    </BaseSection>
  );
});
